import type { ReactElement } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiChevronRight, FiMinimize2, FiZoomIn, FiZoomOut } from 'react-icons/fi';
import { TransformComponent, useControls } from 'react-zoom-pan-pinch';

import { ActionIcon, Button, Center, Group, Image, Stack, Tooltip } from '@highlight-cp/core-ui';

import classes from './asset-gallery-image.module.css';
import type { AssetGalleryImageProps } from './types';

const AssetGalleryImage = ({
  assets,
  currentAsset,
  setCurrentAssetIndex,
}: AssetGalleryImageProps): ReactElement => {
  const { t } = useTranslation();
  const { resetTransform, zoomIn, zoomOut } = useControls();

  const handleNext = useCallback(() => {
    resetTransform();
    setCurrentAssetIndex((prev: number) => (prev + 1) % assets.length);
  }, [assets.length, resetTransform, setCurrentAssetIndex]);

  const handlePrevious = useCallback(() => {
    resetTransform();
    setCurrentAssetIndex((prev: number) => (prev - 1 + assets.length) % assets.length);
  }, [assets.length, resetTransform, setCurrentAssetIndex]);

  if (!currentAsset || !assets.length) return <></>;

  return (
    <Stack className={classes.assetGalleryImage} gap="xs">
      <Center>
        <TransformComponent>
          <Image
            alt={currentAsset.alt || currentAsset.name || t('Asset image')}
            fit="contain"
            src={currentAsset.url}
          />
        </TransformComponent>
      </Center>
      <Group justify="center" wrap="nowrap">
        <Group gap="xs">
          <Tooltip label={t('Zoom in')}>
            <ActionIcon onClick={() => zoomIn()}>
              <FiZoomIn size={24} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('Zoom out')}>
            <ActionIcon onClick={() => zoomOut()}>
              <FiZoomOut size={24} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('Reset zoom')}>
            <ActionIcon onClick={() => resetTransform()}>
              <FiMinimize2 size={24} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      {assets.length > 1 && (
        <Group justify="space-between" wrap="nowrap" grow>
          <Button
            onClick={handlePrevious}
            leftSection={<FiChevronLeft size={16} />}
            variant="subtle"
          >
            {t('Previous')}
          </Button>
          <Button onClick={handleNext} rightSection={<FiChevronRight size={16} />} variant="subtle">
            {t('Next')}
          </Button>
        </Group>
      )}
    </Stack>
  );
};

export default AssetGalleryImage;
